import React from "react";
import "./LeaderboardGlobal.css";

/**
 * RootLayout
 * ----------
 * Provides the animated gradient background layers and a wrapper for page content.
 *
 * Behaviour:
 * - Accepts a `branding` key and exposes its gradient to CSS via --bg-gradient.
 * - Keeps the existing three `.bg` layers so your animation stays untouched.
 */

type BrandingKey = "gemba" | "actica" | "ntegra";

type Props = {
  children: React.ReactNode;
  /** Branding theme to apply to the animated background (defaults to 'gemba'). */
  branding?: BrandingKey;
};

// Map brand keys -> gradients
const GRADIENTS: Record<BrandingKey, string> = {
  gemba: "linear-gradient(-60deg, #37ab69 50%, #5871a9 0%)",
  actica: "linear-gradient(-60deg, #c03245 50%, #0d031f 0%)",
  ntegra: "linear-gradient(-60deg, #99e3c5 50%, #0d1a27 0%)",
};

// Solid brand accents for leaderboard chrome (room-select input, row
// background, dropdown menu, and the "other rank" time badge) that
// previously ignored branding entirely and used a fixed navy/green
// regardless of event (ERD-20). Gemba keeps its long-standing look
// unchanged; actica/ntegra derive from their own gradient stops above.
const ACCENTS: Record<BrandingKey, { panel: string; muted: string }> = {
  gemba: { panel: "#2d3e50", muted: "#3f8856" },
  actica: { panel: "#0d031f", muted: "#681a32" },
  ntegra: { panel: "#0d1a27", muted: "#537f76" },
};

// Helper type to allow CSS custom properties in `style`
type CSSVars = React.CSSProperties & {
  "--bg-gradient": string;
  "--brand-panel": string;
  "--brand-muted": string;
};

const RootLayout: React.FC<Props> = ({ children, branding = "gemba" }) => {
  const cssVars: CSSVars = {
    "--bg-gradient": GRADIENTS[branding], // <-- literal key, no ESLint warning
    "--brand-panel": ACCENTS[branding].panel,
    "--brand-muted": ACCENTS[branding].muted,
  };

  return (
    <div className="leaderboard-scope" style={cssVars}>
      {/* Background layers (animated in CSS) */}
      <div className="bg" />
      <div className="bg bg2" />
      <div className="bg bg3" />

      {/* Main content */}
      <div className="leaderboard-content">{children}</div>
    </div>
  );
};

export default RootLayout;


 